"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getRootPublicKey = exports.sign = void 0;
const accounts_1 = require("@near-js/accounts");
const keystores_1 = require("@near-js/keystores");
const transactions_1 = require("@near-js/transactions");
const bn_js_1 = __importDefault(require("bn.js"));
const ethers_1 = require("ethers");
const relayer_1 = require("../relayer");
const utils_1 = require("../kdf/utils");
const NEAR_MAX_GAS = new bn_js_1.default('300000000000000');
const toRVS = (signature) => {
    return {
        r: signature.big_r.affine_point.substring(2),
        s: signature.s.scalar,
        v: signature.recovery_id,
    };
};
const getMultichainContract = (account, contract) => {
    return new accounts_1.Contract(account, contract, {
        viewMethods: ['public_key'],
        changeMethods: ['sign'],
        useLocalViewExecution: false,
    });
};
/**
 * Signs a transaction hash using a specified account and path, then sends the signed transaction
 * to a relayer service for execution. It attempts to fetch the signature from the transaction
 * receipt up to 3 times with a delay of 10 seconds between each attempt.
 *
 * @param {SignParams} params - The parameters object.
 * @param {string | ethers.BytesLike} params.transactionHash - The hash of the transaction to be signed.
 * @param {string} params.path - The derivation path used for signing the transaction.
 * @param {NearAuthentication} params.nearAuthentication - The NEAR accountId, keypair, and networkId used for signing the transaction.
 * @param {ChainSignatureContracts} params.contract - The contract identifier for chain signature operations.
 * @param {string} [params.relayerUrl] - The URL of the relayer service to which the signed transaction is sent.
 * @returns {Promise<RSVSignature>} A promise that resolves to the RSV signature of the signed transaction.
 * @throws {Error} Throws an error if the signature cannot be fetched after 3 attempts.
 */
const sign = (_a) => __awaiter(void 0, [_a], void 0, function* ({ transactionHash, path, nearAuthentication, contract, relayerUrl, }) {
    const keyStore = new keystores_1.InMemoryKeyStore();
    yield keyStore.setKey(nearAuthentication.networkId, nearAuthentication.accountId, nearAuthentication.keypair);
    const connection = accounts_1.Connection.fromConfig({
        networkId: nearAuthentication.networkId,
        provider: {
            type: 'JsonRpcProvider',
            args: {
                url: {
                    testnet: 'https://rpc.testnet.near.org',
                    mainnet: 'https://rpc.mainnet.near.org',
                }[nearAuthentication.networkId],
            },
        },
        signer: { type: 'InMemorySigner', keyStore },
    });
    const account = new accounts_1.Account(connection, nearAuthentication.accountId);
    const payload = Array.from(ethers_1.ethers.getBytes(transactionHash));
    const signArgs = {
        payload,
        path: (0, utils_1.getCanonicalizedDerivationPath)(path),
        key_version: 0,
    };
    if (!relayerUrl) {
        const multichainContractAcc = getMultichainContract(account, contract);
        const signature = yield multichainContractAcc.sign({
            args: { request: signArgs },
            gas: NEAR_MAX_GAS,
            amount: new bn_js_1.default(1),
        });
        return toRVS(signature);
    }
    try {
        const functionCall = transactions_1.actionCreators.functionCall('sign', { request: signArgs }, NEAR_MAX_GAS, new bn_js_1.default(1));
        const signedDelegate = yield account.signedDelegate({
            receiverId: contract,
            actions: [functionCall],
            blockHeightTtl: 60,
        });
        // Remove the cached access key to prevent nonce reuse
        delete account.accessKeyByPublicKeyCache[
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        signedDelegate.delegateAction.publicKey.toString()];
        // TODO: add support for creating the signed delegate using the mpc recovery service with an oidc_token
        const res = yield fetch(`${relayerUrl}/send_meta_tx_async`, {
            method: 'POST',
            mode: 'cors',
            body: JSON.stringify((0, relayer_1.parseSignedDelegateForRelayer)(signedDelegate)),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        const txHash = yield res.text();
        const txStatus = yield account.connection.provider.txStatus(txHash, account.accountId);
        const signature = txStatus.receipts_outcome.reduce((acc, curr) => {
            if (acc) {
                return acc;
            }
            const { status } = curr.outcome;
            return ((typeof status === 'object' &&
                status.SuccessValue &&
                status.SuccessValue !== '' &&
                Buffer.from(status.SuccessValue, 'base64').toString('utf-8')) ||
                '');
        }, '');
        if (signature) {
            const parsedJSONSignature = JSON.parse(signature);
            return toRVS(parsedJSONSignature.Ok);
        }
    }
    catch (e) {
        console.error(e);
    }
    throw new Error('Signature error, please retry');
});
exports.sign = sign;
function getRootPublicKey(contract, nearNetworkId) {
    return __awaiter(this, void 0, void 0, function* () {
        const nearConnection = accounts_1.Connection.fromConfig({
            networkId: nearNetworkId,
            provider: {
                type: 'JsonRpcProvider',
                args: {
                    url: {
                        testnet: 'https://rpc.testnet.near.org',
                        mainnet: 'https://rpc.mainnet.near.org',
                    }[nearNetworkId],
                },
            },
            signer: { type: 'InMemorySigner', keyStore: new keystores_1.InMemoryKeyStore() },
        });
        const nearAccount = new accounts_1.Account(nearConnection, 'dontcare');
        const multichainContractAcc = getMultichainContract(nearAccount, contract);
        return yield multichainContractAcc.public_key();
    });
}
exports.getRootPublicKey = getRootPublicKey;
